// frontend/src/pages/MonitorPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-10
// Updated:      2026-10-09 12:32 BST
//
// Mobile Location Intelligence host sizing fix (2026-10-09 12:32 BST):
//   - The Monitor mobile surface does not use ReactGridLayout; each widget is hosted in a plain
//     fixed-height wrapper derived from the saved desktop h value.
//   - Location Intelligence Overview now receives an auto-height mobile host with a 100dvh minimum
//     so weather, map, key and Intelligence Summary can render in the normal page flow.
//   - Other Monitor widgets retain their existing saved-layout-derived mobile heights unchanged.
//   - Desktop ReactGridLayout sizing, persistence, drag and resize behaviour are unchanged.
//
// Location Intelligence Overview (2026-10-08 15:24 BST):
//   - Added consolidated pinned-location intelligence widget to Monitor picker/rendering.
//   - Existing four intelligence widgets remain unchanged and independently available.
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:  Monitor dashboard page for live operational intelligence widgets.
//               - Based on the JobsPage layout standard.
//               - Intended for live monitoring surfaces only.
//               - Starts with Weather, Flood, Traffic and Works widgets.
//               - Future live widgets such as Roadworks can be added here without
//                 polluting the Jobs workspace.
//
// Mobile Responsiveness (2026-09-04):
//               - Added a dedicated mobile presentation at <= 768px.
//               - Mobile renders configured Monitor widgets in a natural single-column flow.
//               - Mobile ordering follows the persisted desktop layout position (y, then x).
//               - react-grid-layout drag/resize behaviour remains desktop-only.
//               - Mobile rendering never persists layout changes, so viewing Monitor on a phone
//                 cannot alter the user's desktop widget arrangement.
//               - Reflowed the page heading and Add Widget action for compact mobile widths.
//               - Reduced mobile page gutters while preserving intelligence widget behaviour,
//                 localStorage keys, widget registration, and desktop layout behaviour.
//
// Notes:
//               - Uses its own localStorage keys so layout is isolated from JobsPage.
//               - Follows the canonical full-width page + react-grid-layout standard.
//               - Widget ids must stay aligned with WidgetRegistry / DB widget catalog.

import React, { useState, useEffect, useCallback, useRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";
import { markWidgetUsedIn } from "../widgets/WidgetRegistry";

import WeatherSpotWidget from "../widgets/WeatherSpotWidget.jsx";
import FloodIntelligenceWidget from "../widgets/FloodIntelligenceWidget.jsx";
import TrafficIntelligenceWidget from "../widgets/TrafficIntelligenceWidget.jsx";
import WorksIntelligenceWidget from "../widgets/WorksIntelligenceWidget.jsx";
import LocationIntelligenceOverviewWidget from "../widgets/LocationIntelligenceOverviewWidget.jsx";

import { useTheme } from "../context/ThemeContext.jsx";

const STORAGE_KEY_LAYOUT = "monitorLayout";
const STORAGE_KEY_WIDGETS = "monitorWidgets";

const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

const defaultLayout = [
  { i: "weather_spot_widget", x: 0, y: 0, w: 12, h: 8, minW: 6, minH: 6 },
  { i: "flood_intelligence_widget", x: 0, y: 8, w: 12, h: 8, minW: 6, minH: 6 },
  { i: "traffic_intelligence_widget", x: 0, y: 16, w: 12, h: 8, minW: 6, minH: 6 },
  { i: "works_intelligence", x: 0, y: 24, w: 12, h: 8, minW: 6, minH: 6 },
];

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const MonitorPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const gridRef = useRef(null);
  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;
  const [gridWidth, setGridWidth] = useState(1200);


  useEffect(() => {
    const gridNode = gridRef.current;
    if (!gridNode) return undefined;

    const updateWidth = () => {
      setGridWidth(gridNode.offsetWidth || 1200);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(gridNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const resolveWidgetComponent = (id) => {
    switch (id) {
      case "weather_spot_widget":
        return <WeatherSpotWidget />;
      case "flood_intelligence_widget":
        return <FloodIntelligenceWidget />;
      case "traffic_intelligence_widget":
        return <TrafficIntelligenceWidget />;
      case "works_intelligence":
        return <WorksIntelligenceWidget />;
      case "location_intelligence_overview":
        return <LocationIntelligenceOverviewWidget />;
      default:
        return <div style={{ padding: "1rem", color: "red" }}>Unknown widget: {id}</div>;
    }
  };

  useEffect(() => {
    try {
      const storedLayout = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetData = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));

      let isCorrupted = false;
      if (Array.isArray(storedWidgetData)) {
        if (storedWidgetData.some((item) => typeof item !== "string")) {
          isCorrupted = true;
        }
      }

      if (isCorrupted) {
        console.warn("Clearing corrupted MonitorPage localStorage...");
        localStorage.removeItem(STORAGE_KEY_LAYOUT);
        localStorage.removeItem(STORAGE_KEY_WIDGETS);
      }

      if (!isCorrupted && Array.isArray(storedLayout) && Array.isArray(storedWidgetData)) {
        const normalised = normaliseLayout(storedLayout);
        setLayout(normalised);

        const components = {};
        storedWidgetData.forEach((id) => {
          components[id] = resolveWidgetComponent(id);
          markWidgetUsedIn(id, "monitor");
        });

        setWidgets(components);
        console.log("✅ Restored widgets from storage:", storedWidgetData);
        return;
      }
    } catch (err) {
      console.error("Failed to load saved widget layout:", err);
    }

    setLayout(defaultLayout);
    setWidgets({
      weather_spot_widget: <WeatherSpotWidget />,
      flood_intelligence_widget: <FloodIntelligenceWidget />,
      traffic_intelligence_widget: <TrafficIntelligenceWidget />,
      works_intelligence: <WorksIntelligenceWidget />,
    });

    markWidgetUsedIn("weather_spot_widget", "monitor");
    markWidgetUsedIn("flood_intelligence_widget", "monitor");
    markWidgetUsedIn("traffic_intelligence_widget", "monitor");
    markWidgetUsedIn("works_intelligence", "monitor");

    console.log("✅ Loaded default widgets for MonitorPage");
  }, []);

  const handleAddWidget = (widgetId) => {
    if (widgets[widgetId]) {
      alert("Widget already exists");
      return;
    }

    const component = resolveWidgetComponent(widgetId);
    if (!component) {
      alert("Unknown widget");
      return;
    }

    setWidgets((prev) => ({ ...prev, [widgetId]: component }));
    markWidgetUsedIn(widgetId, "monitor");

    setLayout((prev) => {
      const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => l.y + l.h));
      const newY = Math.max(0, bottomY);

      const newLayout = [
        ...prev,
        { i: widgetId, x: 0, y: newY, w: 6, h: 4, minW: 4, minH: 3, maxW: 12 },
      ];

      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
        localStorage.setItem(
          STORAGE_KEY_WIDGETS,
          JSON.stringify([...Object.keys({ ...widgets, [widgetId]: true })])
        );
      } catch (err) {
        console.error("Failed to save layout:", err);
      }

      return newLayout;
    });

    setShowAddModal(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const clone = { ...prev };
      delete clone[widgetId];
      return clone;
    });

    setLayout((prev) => {
      const filtered = prev.filter((item) => item.i !== widgetId);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(filtered));
        localStorage.setItem(
          STORAGE_KEY_WIDGETS,
          JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId))
        );
      } catch (err) {
        console.error("Failed to save layout:", err);
      }
      return filtered;
    });
  };

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
      } catch (err) {
        console.error("Failed to save layout:", err);
      }
    },
    [widgets]
  );

  /*
   * Mobile is a presentation of the existing Monitor configuration, not a
   * second editable layout. Follow the saved desktop visual order without
   * persisting mobile positions back into monitorLayout.
   */
  const mobileWidgetEntries = Object.entries(widgets).sort(([a], [b]) => {
    const aIndex = layout.findIndex((item) => item.i === a);
    const bIndex = layout.findIndex((item) => item.i === b);

    const aLayout = aIndex >= 0 ? layout[aIndex] : null;
    const bLayout = bIndex >= 0 ? layout[bIndex] : null;

    const ay = Number.isFinite(aLayout?.y) ? aLayout.y : Number.MAX_SAFE_INTEGER;
    const by = Number.isFinite(bLayout?.y) ? bLayout.y : Number.MAX_SAFE_INTEGER;
    if (ay !== by) return ay - by;

    const ax = Number.isFinite(aLayout?.x) ? aLayout.x : Number.MAX_SAFE_INTEGER;
    const bx = Number.isFinite(bLayout?.x) ? bLayout.x : Number.MAX_SAFE_INTEGER;
    if (ax !== bx) return ax - bx;

    return aIndex - bIndex;
  });

  const getMobileWidgetHeight = (widgetId) => {
    const item = layout.find((layoutItem) => layoutItem.i === widgetId);
    const h = Number.isFinite(item?.h) ? Math.max(1, item.h) : 8;

    return Math.max(
      320,
      h * rowHeight + Math.max(0, h - 1) * 24
    );
  };

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingTop: isMobile ? "4px" : undefined,
        paddingBottom: isMobile ? "16px" : undefined,
        paddingLeft: isMobile ? "12px" : containerPadding,
        paddingRight: isMobile ? "12px" : containerPadding,
        boxSizing: "border-box",
      }}
    >
      <div className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "58px" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? "12px" : undefined,
            marginBottom: isMobile ? "6px" : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: 0,
              minWidth: 0,
            }}
          >
            Monitor
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: theme.button_action?.normal || "#000",
              color: "#fff",
              padding: isMobile ? "10px 14px" : "10px 18px",
              minHeight: isMobile ? "44px" : undefined,
              whiteSpace: "nowrap",
              borderRadius: "6px",
              fontWeight: "bold",
              fontFamily: "var(--font-body)",
              cursor: "pointer",
              border: "none",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = theme.button_action?.hover || "#333")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = theme.button_action?.normal || "#000")}
            onMouseDown={(e) => (e.target.style.backgroundColor = theme.button_action?.click || "#111")}
            onMouseUp={(e) => (e.target.style.backgroundColor = theme.button_action?.hover || "#333")}
          >
            Add Widget
          </button>
        </div>

        <div ref={gridRef} className="w-full" style={{ width: "100%", minWidth: 0 }}>
          {isMobile ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                width: "100%",
                minWidth: 0,
                paddingBottom: "8px",
              }}
            >
              {mobileWidgetEntries.map(([key, WidgetComp]) => (
                <div
                  key={key}
                  style={
                    key === "location_intelligence_overview"
                      ? {
                          width: "100%",
                          minWidth: 0,
                          minHeight: "100dvh",
                          height: "auto",
                          overflow: "visible",
                        }
                      : {
                          width: "100%",
                          minWidth: 0,
                          height: `${getMobileWidgetHeight(key)}px`,
                          overflow: "hidden",
                        }
                  }
                >
                  <WidgetWrapper
                    title={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    onRemove={() => handleRemoveWidget(key)}
                  >
                    {WidgetComp}
                  </WidgetWrapper>
                </div>
              ))}
            </div>
          ) : (
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              rowHeight={40}
              width={gridWidth}
              draggableHandle=".widget-drag-handle"
              resizeHandles={["se"]}
              isResizable
              isDraggable
              onLayoutChange={onLayoutChange}
              compactType={null}
              preventCollision={false}
              margin={[16, 24]}
            >
              {Object.entries(widgets).map(([key, WidgetComp]) => (
                <div key={key}>
                  <WidgetWrapper
                    title={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    onRemove={() => handleRemoveWidget(key)}
                  >
                    {WidgetComp}
                  </WidgetWrapper>
                </div>
              ))}
            </GridLayout>
          )}
        </div>

        {showAddModal && (
          <AddWidgetModal
            widgets={[
              { id: "weather_spot_widget", friendly_name: "Met Office Weather" },
              { id: "flood_intelligence_widget", friendly_name: "Flood Intelligence" },
              { id: "traffic_intelligence_widget", friendly_name: "Traffic Intelligence" },
              { id: "works_intelligence", friendly_name: "Works Intelligence" },
              { id: "location_intelligence_overview", friendly_name: "Location Intelligence Overview" },
            ]}
            onClose={() => setShowAddModal(false)}
            onSelect={handleAddWidget}
          />
        )}
      </div>
    </div>
  );
};

export default MonitorPage;